import { ReactNode } from 'react'

import { Box, Section, Spinner } from '@factorialco/deprecated-design-system'
import {
  ResourceFieldDataFragment,
  TaskDetailAffecteeReferenceContractFragment,
  TaskDetailContractContextlessPoliciesFragment,
  TaskDetailCustomFieldsContextlessPoliciesFragment,
  TaskDetailJobCatalogContextlessPoliciesFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import users from 'modules/core/store/collections/users'

import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { TaskFile } from 'modules/tasks/hooks/useGetTaskFiles'
import { useMaterializedTaskItem } from 'modules/tasks/hooks/useMaterializedTaskItem'

import Content from './Content'
import Info from './Info'

interface Props {
  task: TaskDetail
  assignees: ReactNode
  dueOn: string | null | undefined
  files: TaskFile[]
  loadingFiles: boolean
  affectee: ReactNode
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  canEmployeeManageAnyEmployeeFolder: boolean
  legalEntityCountry: string
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  referenceContract?: TaskDetailAffecteeReferenceContractFragment | null
  jobCatalogContextlessPolicies: TaskDetailJobCatalogContextlessPoliciesFragment | undefined
  hasPayrollBundle: boolean
  contractContextlessPolicies: TaskDetailContractContextlessPoliciesFragment | undefined
  customFieldsContextlessPolicies: TaskDetailCustomFieldsContextlessPoliciesFragment | undefined
}

const TaskDetails = ({
  task,
  assignees,
  dueOn,
  files,
  loadingFiles,
  affectee,
  canSeePersonalInfo,
  canEditPersonalInfo,
  canEmployeeManageAnyEmployeeFolder,
  legalEntityCountry,
  customResourceFieldsData,
  customValuesData,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  referenceContract,
  jobCatalogContextlessPolicies,
  hasPayrollBundle,
  contractContextlessPolicies,
  customFieldsContextlessPolicies,
}: Props) => {
  const { workflowOwnerId, stepName, mandatory, loading } = useMaterializedTaskItem({
    taskId: task.id,
  })

  const owner = users.get(workflowOwnerId ?? '')

  if (loading)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Section.CustomTwoThirds
      primary={
        <Content
          files={files}
          loadingFiles={loadingFiles}
          task={task}
          owner={owner}
          canSeePersonalInfo={canSeePersonalInfo}
          canEditPersonalInfo={canEditPersonalInfo}
          canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          legalEntityCountry={legalEntityCountry}
          referenceContract={referenceContract}
          customResourceFieldsData={customResourceFieldsData}
          customValuesData={customValuesData}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
          hasPayrollBundle={hasPayrollBundle}
          contractContextlessPolicies={contractContextlessPolicies}
          customFieldsContextlessPolicies={customFieldsContextlessPolicies}
        />
      }
      secondary={
        <Info
          stepName={stepName}
          mandatory={mandatory}
          task={task}
          assignees={assignees}
          affectee={affectee}
          dueOn={dueOn}
        />
      }
    />
  )
}

export default observer(TaskDetails)
